import Alpine from 'alpinejs';
import focus from '@alpinejs/focus';
import { carousel } from './shared/carousel';
import { featuredCarousel } from './shared/featuredCarousel';
import { heroCarousel } from './shared/heroCarousel';
import { gallery } from './shared/gallery';
import { feedRefresh } from './shared/feedRefresh';

declare global {
  interface Window {
    Alpine: typeof Alpine;
  }
}

/**
 * Lock / unlock page scroll while a full-screen offcanvas is open. Lenis drives
 * the real scroll, so native `overflow:hidden` alone won't stop it — we stop the
 * Lenis instance (exposed on `window.lenis` in app.ts) and add a backstop class
 * on <html>. Safe to call before Lenis exists (optional-chained).
 */
/**
 * Sticky header stack the anchor menu must clear when scrolling to a section:
 * header-nav (60px) + item-full-nav (60px) + a 30px breathing margin. Used as
 * the Lenis scroll-landing offset so a clicked section lands just below the
 * sticky stack.
 */
const ANCHOR_NAV_OFFSET = 150;

/**
 * Scrollspy activation line, as a % of the viewport height from the top. A
 * section becomes active the moment its title scrolls up past this line, so the
 * highlight tracks the title closely rather than only flipping once you've
 * scrolled well into the section. Kept clear of the sticky header stack above.
 */
const ANCHOR_ACTIVATE_PCT = 30;

const lockScroll = (lock: boolean): void => {
  const lenis = (window as any).lenis;
  document.documentElement.classList.toggle('is-scroll-locked', lock);
  if (lock) {
    lenis?.stop();
  } else {
    lenis?.start();
  }
};

/**
 * Alpine bootstrap — register plugins, stores and data factories here, then
 * start Alpine. Anything Alpine-related lives in this file so app.ts stays a
 * thin bootstrap. New `Alpine.data(...)` factories and `Alpine.store(...)`
 * stores must be registered BEFORE `Alpine.start()`.
 */
export const initAlpine = () => {
  window.Alpine = Alpine;
  Alpine.plugin(focus);

  /**
   * Offcanvas store — coordinates a toggle button with its panel.
   *
   * Toggle and panel usually live in separate DOM subtrees (e.g. the toggle
   * sits in the top bar inside its own stacking context, while the panel is a
   * fixed full-screen sibling underneath it), so a global store keyed by a
   * string `id` wires them together. Only one offcanvas is open at a time —
   * opening another closes the current one.
   *
   * Markup contract (both elements need an `x-data` ancestor):
   *   toggle:  @click="$store.offcanvas.toggle('feedfpff')"
   *            :aria-expanded="$store.offcanvas.isOpen('feedfpff')"
   *   panel:   x-show="$store.offcanvas.isOpen('feedfpff')"
   *            x-trap="$store.offcanvas.isOpen('feedfpff')"
   *            @keydown.escape.window="$store.offcanvas.close()"
   *
   * Opening any offcanvas locks page scroll (panels are full-screen); closing
   * restores it. See `lockScroll()` above.
   */
  Alpine.store('offcanvas', {
    active: null as string | null,
    isOpen(id: string): boolean {
      return this.active === id;
    },
    toggle(id: string): void {
      this.active === id ? this.close() : this.open(id);
    },
    open(id: string): void {
      this.active = id;
      lockScroll(true);
    },
    close(): void {
      this.active = null;
      lockScroll(false);
    },
  });

  /**
   * Dropdown — lightweight inline dropdown. Scope is self-contained so it
   * works inside any `x-data` ancestor. Register @click.outside="close()" on
   * the root element to close when the user clicks away.
   */
  Alpine.data('dropdown', () => ({
    open: false as boolean,
    toggle(): void { this.open = !this.open; },
    close(): void { this.open = false; },
  }));

  /**
   * Submenu — collapse state for a single mobile-menu section row (toggle button
   * + its inline child list). Independent per instance (several sections may be
   * open at once); pass the initial state so the active branch can start open:
   *   x-data="submenu(true)"
   * Markup contract: `@click="toggle()"` + `:aria-expanded` on the button, and
   * `:class="open ? '…' : '…'"` + `:inert="!open"` on the collapsible panel.
   * Consumer: `section_nav_mobile_menu_link` / `item_nav_mobile_submenu`.
   */
  Alpine.data('submenu', (initialOpen = false) => ({
    open: initialOpen as boolean,
    toggle(): void { this.open = !this.open; },
  }));

  /**
   * Anchor menu — the sticky "kotwice" submenu of a PAGEFPFF page. Smooth-scrolls
   * to a child section and keeps the active menu item in sync with scroll.
   *
   * The menu (`pagefpff_anchor_menu`) and its target sections live in separate
   * columns under a shared `x-data="anchorMenu"` wrapper (`pagefpff_main_footer`).
   * Each target is an `.item-list-content` carrying `id="al<childID>"`
   * (Item::ANCHOR_ID_PREFIX + id); menu links pass that same id to `scrollTo()`.
   *
   * - scrollTo(id): Lenis-driven smooth scroll with a strong ease-out (easeOutExpo),
   *   landing the target just below the sticky header stack (ANCHOR_NAV_OFFSET).
   *   It also locks the scrollspy for the duration of the animation so the clicked
   *   item highlights instantly and stays put, instead of flickering through the
   *   sections the scroll passes over and only settling once you nudge further.
   * - active state: an IntersectionObserver scrollspy marks the last section whose
   *   title has scrolled up past the activation line (ANCHOR_ACTIVATE_PCT of the
   *   viewport). The IO band is collapsed to that single line, so the callback
   *   fires exactly as a title crosses it. Consumers bind `isActive(id)` to the
   *   `link-underline` `.is-opened` modifier so active == hovered styling.
   */
  Alpine.data('anchorMenu', () => ({
    active: null as string | null,
    _io: null as IntersectionObserver | null,
    _lock: false as boolean,
    _unlock: 0 as number,
    init(): void {
      const targets = Array.from(
        document.querySelectorAll<HTMLElement>('.pagefpff-anchor-children .item-list-content[id]')
      );
      if (!targets.length) return;
      // Active = the last section (document order) whose top has scrolled up past
      // the activation line. Targets are in document order, so their tops are
      // monotonic — stop at the first one still below the line.
      const sync = (): void => {
        if (this._lock) return; // a click-driven scroll owns `active` until it lands
        const line = window.innerHeight * (ANCHOR_ACTIVATE_PCT / 100);
        let current = targets[0].id;
        for (const t of targets) {
          if (t.getBoundingClientRect().top > line) break;
          current = t.id;
        }
        this.active = current;
      };
      this._io = new IntersectionObserver(sync, {
        rootMargin: `-${ANCHOR_ACTIVATE_PCT}% 0px -${100 - ANCHOR_ACTIVATE_PCT}% 0px`,
        threshold: 0,
      });
      targets.forEach((t) => this._io!.observe(t));
      sync(); // initial state
    },
    destroy(): void {
      this._io?.disconnect();
      clearTimeout(this._unlock);
    },
    isActive(id: string): boolean {
      return this.active === id;
    },
    scrollTo(id: string): void {
      const target = document.getElementById(id);
      if (!target) return;
      this.active = id; // instant feedback
      this._lock = true; // freeze the scrollspy so it doesn't flicker mid-scroll
      clearTimeout(this._unlock);
      const release = (): void => {
        this._lock = false;
      };
      const lenis = (window as any).lenis;
      if (lenis) {
        lenis.scrollTo(target, {
          offset: -ANCHOR_NAV_OFFSET,
          duration: 0.7,
          easing: (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)), // easeOutExpo
          onComplete: release,
        });
        // Fallback: Lenis can skip onComplete if the scroll is clamped/interrupted.
        this._unlock = window.setTimeout(release, 900);
      } else {
        target.scrollIntoView({ behavior: 'smooth' });
        this._unlock = window.setTimeout(release, 900);
      }
    },
  }));

  /**
   * Calendar filters — the day-listing's collapsible taxonomy panel. The toggle
   * button lives in the calendar's own sticky bar (`.calendar-main-sticky`,
   * pinned below the global header), while the panel (`#calendar-filters`) sits
   * in normal flow just beneath it. Put `x-data="calendarFilters"` on
   * `.calendar-main` so both share `filtersOpen`.
   *
   * On open, the panel is scrolled into view: when the user has scrolled down,
   * the panel's flow position is above the viewport, so expanding it alone shows
   * nothing. We land the panel just below the calendar sticky bar's bottom edge —
   * measured live, so it inherently clears everything stacked above it (global
   * header + this bar) without hard-coding offsets. Skipped when the panel is
   * already visible below the bar (e.g. at the top of the page).
   *
   * `panelOpen` gates the clip: the collapse animates via a CSS-grid `0fr↔1fr`
   * height, which needs `overflow-hidden` to clip the rows mid-animation — but
   * that also clips the absolutely-positioned taxonomy dropdowns. So we clip only
   * while animating and switch to `overflow-visible` once the panel has finished
   * opening (set on the grid's `transitionend`), re-clipping the instant it
   * starts to collapse. With reduced motion there's no transition, so we reveal
   * the overflow immediately.
   *
   * `initialOpen` starts the panel expanded when filters are already in use, so
   * an active filter set isn't hidden behind a collapsed toggle on page load:
   *   x-data="calendarFilters(true)"
   * It starts open with no entry animation, so `panelOpen` is set straightaway
   * (no `transitionend` fires) to let the dropdowns overflow from the first paint.
   */
  Alpine.data('calendarFilters', (initialOpen = false) => ({
    filtersOpen: initialOpen as boolean,
    panelOpen: initialOpen as boolean,
    toggle(): void {
      this.filtersOpen = !this.filtersOpen;
      if (!this.filtersOpen) {
        this.panelOpen = false; // re-clip while collapsing
        return;
      }
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.panelOpen = true; // no height transition → no transitionend to wait for
      }
      const root = (this as any).$root as HTMLElement;
      const bar = root.querySelector<HTMLElement>('.calendar-main-sticky');
      const panel = root.querySelector<HTMLElement>('#calendar-filters');
      if (!bar || !panel) return;
      const barBottom = bar.getBoundingClientRect().bottom;
      // Already sitting below the bar → nothing hidden, no scroll needed.
      if (panel.getBoundingClientRect().top >= barBottom) return;
      const offset = barBottom + 20; // breathing gap under the sticky bar
      const lenis = (window as any).lenis;
      if (lenis) {
        lenis.scrollTo(panel, {
          offset: -offset,
          duration: 0.7,
          easing: (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)), // easeOutExpo
        });
      } else {
        const top = panel.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    },
  }));

  /**
   * Carousel — reusable scroll-snap carousel (thumbnail strips, content sliders).
   * Owns only behaviour (active slide, arrow nav, the per-slide `is-current`
   * flag); the visual layout is CSS/utility classes on the markup. See
   * `shared/carousel.ts` for the full markup contract.
   */
  Alpine.data('carousel', carousel);

  /**
   * Featured carousel — "featured + rail" slider where the active slide widens
   * to the 8-col slot and advancing promotes the next slide into it (two boxes
   * always on screen). All geometry is CSS (`--narrow`/`--ci`); JS only writes
   * the current index. Drives `modreports_main`. See `shared/featuredCarousel.ts`.
   */
  Alpine.data('featuredCarousel', featuredCarousel);

  /**
   * Hero carousel — the homepage opener (`modcarousel_main`): one full-stage
   * slide at a time, stacked in a single grid cell so the stage auto-sizes to
   * the tallest slide and the nav arrows never move. Advancing slides the panel
   * out on the X axis while the background cross-fades. JS writes only the
   * current index (`--ci`); the rest is CSS. See `shared/heroCarousel.ts`.
   */
  Alpine.data('heroCarousel', heroCarousel);

  /**
   * Gallery — a `carousel` (thumbnail strip) plus a shared full-size lightbox
   * `<dialog>`. Drives both `modgallery` display modes (`list_main`,
   * `list_content`); deep-links / shares images via `#photo-{id}`. See
   * `shared/gallery.ts`.
   */
  Alpine.data('gallery', gallery);

  /**
   * Feed refresh — keeps the open Live-feed offcanvas current by polling its
   * `.ajax` fragment every 5 min while visible (paused when the tab is hidden /
   * the panel is closed). Started/stopped from the offcanvas store via x-effect.
   * See `shared/feedRefresh.ts`.
   */
  Alpine.data('feedRefresh', feedRefresh);

  Alpine.start();
};
